import { PlusIcon } from '@phosphor-icons/react';
import { captureException } from '@sentry/react';
import { Button } from 'components/button/Button';
import { Card } from 'components/Card';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { Input } from '../../../../../components/input/Input';
import { getDisplayError } from '../../../../../utils/get-display-error';
import { decimateAndFormatNumber, parseNumberInput } from '../../../../../utils/number';
import { updateGeneralSettings } from '../../../models';

interface DieselSurchargeSectionProps {
  currentPercentage: number;
  onSaved: () => void;
}

/**
 * Default diesel surcharge for carriers. Saving it also updates every carrier
 * that uses the diesel surcharge without a custom percentage.
 */
export const DieselSurchargeSection: React.FC<DieselSurchargeSectionProps> = ({ currentPercentage, onSaved }) => {
  const initialValue = decimateAndFormatNumber(currentPercentage, 2);
  const [value, setValue] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);

  const hasChanged = value !== initialValue;

  const handleSave = async () => {
    const numValue = parseFloat(value.replace(',', '.'));
    if (isNaN(numValue) || numValue < 0 || numValue > 100) {
      toast.error('Percentage moet tussen 0 en 100 liggen');
      return;
    }
    setIsSaving(true);
    try {
      await updateGeneralSettings({ dieselSurchargePercentage: parseNumberInput(value, 2) });
      toast.success('Diesel toeslag aangepast');
      onSaved();
    } catch (err) {
      captureException(err);
      toast.error('Kon diesel toeslag niet aanpassen: ' + getDisplayError(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card
      title="Standaard diesel toeslag"
      subtitle="Wordt toegepast op vervoerders met diesel toeslag zonder eigen percentage. 0 = geen toeslag."
    >
      <div className="flex items-center gap-4">
        <div className="w-48 text-sm font-medium text-fg">Percentage (%)</div>
        <div className="w-32">
          <Input type="number" step="0.01" value={value} onChange={setValue} />
        </div>
        {hasChanged && (
          <Button
            type="button"
            color="primary"
            onTrigger={handleSave}
            isDisabled={isSaving}
            isLoading={isSaving}
            iconLeft={<PlusIcon className="button-icon" />}
          >
            Opslaan
          </Button>
        )}
      </div>
    </Card>
  );
};
